import { Info, TrendingDown, TrendingUp, Truck } from 'lucide-react';
import { Badge, Skeleton, Text, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@musa.tech/design-system';
import { fmtWeight } from '@/utils/formatWeight';

interface TotalColetadoCardProps {
  value: number;
  change: number | null;
  isLoading?: boolean;
}

const formatChange = (c: number) => {
  const sign = c >= 0 ? '+' : '';
  return `${sign}${c.toFixed(1)}%`;
};

export const TotalColetadoCard = ({ value, change, isLoading }: TotalColetadoCardProps) => {
  if (isLoading) return <Skeleton className="h-20 w-full rounded-xl" />;

  const isPositive = change !== null && change >= 0;
  const { value: fv, unit } = fmtWeight(value);

  return (
    <div className="relative overflow-hidden rounded-xl bg-primary-200 border border-primary-200 pl-6 pr-14 py-4">
      <div className="flex items-center justify-between gap-6">
        {/* Esquerda: título + badge */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1.5">
            <Text variant="heading" size="large" className="text-foreground">
              Total Coletado
            </Text>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-3.5 h-3.5 cursor-help opacity-70 text-foreground" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs leading-relaxed">
                  O total coletado e os cálculos consideram exclusivamente resíduos recicláveis: metais, plásticos, papel e vidro.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {change !== null ? (
            <Badge
              variant={isPositive ? 'success' : 'destructive'}
              className="w-fit !bg-info-100 !text-info-700 !border-info-100"
            >
              <span className="flex items-center gap-1 text-xs leading-tight">
                {isPositive ? (
                  <TrendingUp className="w-2.5 h-2.5" />
                ) : (
                  <TrendingDown className="w-2.5 h-2.5" />
                )}
                {formatChange(change)} vs período anterior
              </span>
            </Badge>
          ) : (
            <Badge variant="success" className="w-fit opacity-0 pointer-events-none select-none" aria-hidden="true">
              <span className="text-xs leading-tight">&nbsp;</span>
            </Badge>
          )}
        </div>

        {/* Direita: valor + unidade inline */}
        <div className="flex items-end gap-1.5 shrink-0">
          <Text variant="heading" className="text-4xl font-bold text-foreground leading-none">
            {fv}
          </Text>
          <Text variant="body" size="small" className="text-muted-foreground mb-0.5">
            {unit}
          </Text>
        </div>
      </div>
      <Truck className="absolute right-6 top-1/2 -translate-y-1/2 w-20 h-20 text-foreground opacity-10 pointer-events-none" />
    </div>
  );
};
